import React, { useEffect, useState } from 'react';
import portalService from '../../services/portalService';
import { useLanguage, t } from '../../context/LanguageContext';

function periodLabel(from, to) {
  return `${from} — ${to}`;
}

function currentPeriodBounds() {
  const now = new Date();
  const day = now.getDate();
  const y = now.getFullYear(), m = now.getMonth();
  const from = day <= 15 ? new Date(y, m, 1) : new Date(y, m, 16);
  const to   = day <= 15 ? new Date(y, m, 15) : new Date(y, m + 1, 0);
  return { from: from.toISOString().split('T')[0], to: to.toISOString().split('T')[0] };
}

function prevPeriodBounds(from, to) {
  const f = new Date(from);
  f.setDate(f.getDate() - 1);
  const prevTo = f.toISOString().split('T')[0];
  const prevFrom = f.getDate() <= 15
    ? new Date(f.getFullYear(), f.getMonth(), 1).toISOString().split('T')[0]
    : new Date(f.getFullYear(), f.getMonth(), 16).toISOString().split('T')[0];
  return { from: prevFrom, to: prevTo };
}

function fmt(dt, lang) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString(lang === 'es' ? 'es-MX' : 'en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function PortalHoursPage() {
  const { lang } = useLanguage();
  const [bounds, setBounds] = useState(currentPeriodBounds());
  const [entries, setEntries] = useState([]);
  const [ptoHours, setPtoHours] = useState(0);
  const [sickHours, setSickHours] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    portalService.getHours(bounds.from, bounds.to)
      .then(data => {
        const sorted = [...(data.entries || [])].sort((a, b) => {
          const da = a.entryKind === 'shift' ? a.clockIn : a.date;
          const db = b.entryKind === 'shift' ? b.clockIn : b.date;
          return new Date(db) - new Date(da);
        });
        setEntries(sorted);
        setPtoHours(data.ptoHours || 0);
        setSickHours(data.sickHours || 0);
      })
      .catch(() => { setEntries([]); setPtoHours(0); setSickHours(0); })
      .finally(() => setLoading(false));
  }, [bounds]);

  const totalHours = entries.reduce((s, e) => s + (e.netHours || 0), 0);

  const goBack = () => setBounds(prev => prevPeriodBounds(prev.from, prev.to));
  const goForward = () => {
    const next = new Date(bounds.to);
    next.setDate(next.getDate() + 1);
    const y = next.getFullYear(), m = next.getMonth(), d = next.getDate();
    const from = d <= 15 ? new Date(y, m, 1) : new Date(y, m, 16);
    const to   = d <= 15 ? new Date(y, m, 15) : new Date(y, m + 1, 0);
    setBounds({ from: from.toISOString().split('T')[0], to: to.toISOString().split('T')[0] });
  };

  return (
    <div>
      <h5 className="fw-bold mb-3">{t('My Hours', 'Mis horas', lang)}</h5>

      {/* Period nav */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <button className="btn btn-outline-secondary btn-sm" onClick={goBack}>&larr;</button>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{periodLabel(bounds.from, bounds.to)}</span>
        <button className="btn btn-outline-secondary btn-sm" onClick={goForward}>&rarr;</button>
      </div>

      {loading ? (
        <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary" /></div>
      ) : entries.length === 0 ? (
        <div className="text-muted text-center py-4" style={{ fontSize: 13 }}>{t('No entries for this period.', 'Sin registros para este período.', lang)}</div>
      ) : (
        <>
          <div className="card mb-3">
            <div className="card-body py-2 px-3">
              <div className="d-flex justify-content-between">
                <span style={{ fontSize: 13 }}>{t('Shifts', 'Turnos', lang)}: <strong>{entries.filter(e => e.entryKind === 'shift').length}</strong></span>
                <span style={{ fontSize: 13 }}>{t('Total', 'Total', lang)}: <strong>{Math.round(totalHours * 100) / 100} h</strong></span>
              </div>
              {(ptoHours > 0 || sickHours > 0) && (
                <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                  {t('Includes', 'Incluye', lang)} {[
                    ptoHours > 0 && `${ptoHours}h PTO`,
                    sickHours > 0 && `${sickHours}h ${t('Sick', 'Enfermedad', lang)}`,
                  ].filter(Boolean).join(' + ')}
                </div>
              )}
            </div>
          </div>
          {entries.map(e => (
            <div key={`${e.entryKind}-${e.id}`} className="card mb-2">
              <div className="card-body py-2 px-3">
                {e.entryKind !== 'shift' ? (
                  <div className="d-flex justify-content-between align-items-center">
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      {new Date(e.date + 'T12:00:00').toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      <span className={`badge ms-2 ${e.entryKind === 'sick' ? 'text-bg-danger' : 'text-bg-success'}`} style={{ fontSize: 10 }}>
                        {e.entryKind === 'sick' ? t('Sick', 'Enfermedad', lang) : 'PTO'}
                      </span>
                    </span>
                    <span className="badge text-bg-secondary">{e.netHours} h</span>
                  </div>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-center">
                      <span style={{ fontSize: 13, fontWeight: 600 }}>
                        {new Date(e.clockIn).toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                      {e.netHours != null
                        ? <span className="badge text-bg-primary">{e.netHours} h</span>
                        : <span className="badge text-bg-warning text-dark">{t('Open', 'Abierto', lang)}</span>}
                    </div>
                    <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                      {fmt(e.clockIn, lang)} &rarr; {fmt(e.clockOut, lang)}
                      {e.breakMinutes > 0 && <span className="ms-2">· {e.breakMinutes} {t('min break', 'min descanso', lang)}</span>}
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
